import type { JSX } from 'react';
import { useEffect, useState, useRef } from 'react';
import Loader from '@/components/Loader';

import { useLazyGetWeeklyPredictionQuery } from '@/store/api/WeeklyPredictions/weeklyPredictionApi';

import { useLazyGetMonthlyPredictionQuery } from '@/store/api/MonthlyPredictions/monthlyPredictionApi';

interface HoroscopeData {
  title: string;
  content: string;
}                    
const RASHIS = [
  { en: 'Aries', hi: 'Mesha' },
  { en: 'Taurus', hi: 'Vrishabha' },
  { en: 'Gemini', hi: 'Mithuna' },
  { en: 'Cancer', hi: 'Karkataka' },
  { en: 'Leo', hi: 'Simha' },
  { en: 'Virgo', hi: 'Kanya' },
  { en: 'Libra', hi: 'Tula' },
  { en: 'Scorpio', hi: 'Vrishchika' },
  { en: 'Sagittarius', hi: 'Dhanur' },
  { en: 'Capricorn', hi: 'Makar' },
  { en: 'Aquarius', hi: 'Kumbha' },
  { en: 'Pisces', hi: 'Meena' },
];

export default function Prediction(): JSX.Element {
  const [activeRashi, setActiveRashi] = useState<string>(RASHIS[0]!.en);
  const [isMobileRashiOpen, setIsMobileRashiOpen] = useState(false);

  const [predictionData, setPredictionData] = useState<HoroscopeData[]>([]);
  const [activeTab, setActiveTab] = useState<'weekly' | 'monthly'>('weekly');
  const [error, setError] = useState<boolean>(false);
  const predictionTopRef = useRef<HTMLDivElement | null>(null);

  const [getWeeklyPrediction, weeklyState] = useLazyGetWeeklyPredictionQuery();

  const [getMonthlyPrediction, monthlyState] = useLazyGetMonthlyPredictionQuery();

  const loading = weeklyState.isFetching || monthlyState.isFetching;

  const formatPredictionText = (raw: string): HoroscopeData[] => {
    if (!raw) return [];

    const sections = raw
      .split(/\n\s*\n/)
      .map((p) => p.trim())
      .filter(Boolean);

    const formatted: HoroscopeData[] = [];

    sections.forEach((block) => {
      const lines = block.split('\n').filter((l) => l.trim() !== '');
      if (!lines.length) return;

      formatted.push({
        title: lines[0]?.trim() ?? '',
        content: lines.slice(1).join(' ').trim(),
      });
    });

    return formatted;
  };

  const fetchPrediction = async () => {
    setError(false);
    setPredictionData([]);

    try {
      const res =
        activeTab === 'weekly'
          ? await getWeeklyPrediction({ rashi: activeRashi }).unwrap()
          : await getMonthlyPrediction({ rashi: activeRashi }).unwrap();

      if (!res?.prediction) {
        setError(true);
        return;
      }

      const formatted = formatPredictionText(res.prediction);
      setPredictionData(formatted);
    } catch (err) {
      console.error('Prediction error:', err);
      setError(true);
    }
  };

  useEffect(() => {
    fetchPrediction();
    requestAnimationFrame(() => {
      predictionTopRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, [activeRashi, activeTab]);

  const activeRashiLabel = RASHIS.find((r) => r.en === activeRashi)?.hi + ' / ' + activeRashi;

  const activeRashiObj = RASHIS.find((r) => r.en === activeRashi);
  const headingText = `${activeRashiObj?.hi?.toUpperCase()} RASHI (${activeRashiObj?.en.toUpperCase()} MOON SIGN)`;

  return (
    <section className="min-h-screen w-full lg:py-26 text-black font-spectral">
      <div className="max-w-[1600px] flex px-5 mx-auto bg-white gap-8 py-10">
        <aside className="hidden lg:block w-[260px] shrink-0 sticky top-16 self-start">
          <h3 className="font-spectral font-semibold text-[#082261] text-xl mb-4">SELECT RASHI</h3>

          <div className="flex flex-col gap-2 uppercase">
            {RASHIS.map((rashi) => (
              <button
                key={rashi.en}
                onClick={() => setActiveRashi(rashi.en)}
                className={`px-4 py-2 rounded-lg text-left font-spectral transition-all ${
                  activeRashi === rashi.en
                    ? 'bg-[linear-gradient(to_right,#082261,#34065c)] text-white'
                    : 'bg-white inner-shadow-blue text-black'
                }`}
              >
                {rashi.hi} / {rashi.en}
              </button>
            ))}
          </div>
        </aside>

        <div className="flex flex-col gap-8 w-full">
          <div ref={predictionTopRef}></div>
          <div className="flex justify-center mb-8 gap-4">
            <button
              onClick={() => setActiveTab('weekly')}
              className={`px-6 py-2 rounded-lg font-semibold transition-all uppercase  ${
                activeTab === 'weekly'
                  ? 'bg-gradient-to-r from-[#082261] to-[#34065c] text-white'
                  : 'bg-white inner-shadow-blue text-black'
              }`}
            >
              Weekly Predictions
            </button>

            <button
              onClick={() => setActiveTab('monthly')}
              className={`px-6 py-2 rounded-lg font-semibold transition-all uppercase ${
                activeTab === 'monthly'
                  ? 'bg-gradient-to-r from-[#082261] to-[#34065c] text-white'
                  : 'bg-white inner-shadow-blue text-black'
              }`}
            >
              Monthly Predictions
            </button>
          </div>

          <div className="lg:hidden mb-6">
            <button
              onClick={() => setIsMobileRashiOpen((prev) => !prev)}
              className="w-full px-4 py-3 rounded-lg flex justify-between items-center
               bg-white inner-shadow-blue font-spectral font-semibold text-black"
            >
              <span>
                {activeRashiObj?.hi} / {activeRashiObj?.en}
              </span>
              <span className="text-sm">{isMobileRashiOpen ? '▲' : '▼'}</span>
            </button>
            {isMobileRashiOpen && (
              <div className="mt-2 max-h-[300px] overflow-y-auto rounded-lg bg-white inner-shadow-blue">
                {RASHIS.map((rashi) => (
                  <button
                    key={rashi.en}
                    onClick={() => {
                      setActiveRashi(rashi.en);
                      setIsMobileRashiOpen(false);
                    }}
                    className={`w-full px-4 py-2 text-left font-spectral transition-all ${
                      activeRashi === rashi.en
                        ? 'bg-[linear-gradient(to_right,#082261,#34065C)] text-white'
                        : 'text-black'
                    }`}
                  >
                    {rashi.hi} / {rashi.en}
                  </button>
                ))}
              </div>
            )}
          </div>
          <main className="flex-1">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 mb-6">
              <h2 className="font-28 font-semibold font-spectral uppercase bg-clip-text text-transparent bg-gradient-to-r from-[#082261] to-[#34065c]">
                {activeTab === 'weekly' ? 'Weekly' : 'Monthly'} Predictions – {headingText}
              </h2>
            </div>

            {loading ? (
              <div className="text-[#082261] font-spectral text-lg">
                <Loader />
              </div>
            ) : error ? (
              <div className="text-[#082261] font-spectral text-lg">
                Unable to load predictions. Please try again later.
              </div>
            ) : predictionData.length ? (
              <div className="space-y-6">
                {predictionData.map((section, idx) => (
                  <section key={idx}>
                    <h3 className="font-spectral text-[#001B6B] font-semibold text-[20px] uppercase tracking-wide">
                      {section.title}
                    </h3>
                    <p className="font-lato text-[#333333] text-[18px] leading-relaxed mt-2">
                      {section.content}
                    </p>
                  </section>
                ))}
              </div>
            ) : (
              <div className="text-[#082261] font-spectral text-lg">No prediction available.</div>
            )}
          </main>
        </div>
      </div>
    </section>
  );
}
